import { canEditSection } from '@/lib/access';
import React, { useEffect, useState } from 'react';
import { useManualForm, FormShell, NumberField, TextAreaField, CheckboxField, ReadOnlyField } from './_shared';
import { Card } from '@/components/UI';
import { StatusBadge, IconChip } from '@/components/design';
import { useHrMrm, sourceLabel } from '@/components/HrSnapshot';
import api from '@/lib/api';
import { Database, RefreshCw } from 'lucide-react';

const DEFAULT = {
  summary: { total_employees: 0, new_joiners: 0, separations: 0, personnel_expenses: 0, personnel_expenses_pct_of_sales: 0, target_personnel_pct: 13.5, ot_hours: 0, ot_target: 10000, attrition_rate_pct: 0, attrition_target_pct: 2.7, training_hours: 0, training_target_hours: 500, suggestions_received: 0, suggestions_implemented: 0, kaizens: 0, poka_yokes: 0, accidents_lti: 0, accidents_non_lti: 0, near_misses: 0, pf_compliance: true, esi_compliance: true, notes: '' },
};

// manual form key -> normalized HR field key (the /data/hr-mrm object)
const HR_KEY = {
  total_employees: 'employee_count', new_joiners: 'new_joiners', separations: 'exits', personnel_expenses: 'personnel_expenses',
  ot_hours: 'overtime_hours', attrition_rate_pct: 'attrition_pct', training_hours: 'training_hours',
  suggestions_received: 'suggestions_received', suggestions_implemented: 'suggestions_implemented', kaizens: 'kaizens',
  poka_yokes: 'poka_yokes', accidents_lti: 'lti', accidents_non_lti: 'non_lti_accidents', near_misses: 'near_miss',
  pf_compliance: 'pf_compliance', esi_compliance: 'esi_compliance',
};

const GROUPS = [
  { title: 'Manpower (HR software)', keys: ['employee_count', 'permanent_employees', 'contract_employees', 'new_joiners', 'exits', 'attrition_pct', 'open_vacancies', 'offers_issued', 'promotions', 'transfers_in', 'transfers_out', 'confirmations'] },
  { title: 'Attendance (HR software)', keys: ['attendance_pct', 'absenteeism_pct', 'absent_employees', 'leave_days', 'overtime_hours', 'working_hours', 'mandays'] },
  { title: 'Training (HR software)', keys: ['trainings_conducted', 'training_hours', 'employees_trained', 'training_completion_pct', 'training_effectiveness_pct'] },
  { title: 'Safety (HR software)', keys: ['accidents', 'lti', 'non_lti_accidents', 'first_aid', 'medical_cases', 'fatalities', 'near_miss', 'unsafe_acts', 'unsafe_conditions', 'safety_observations', 'safety_training_hours', 'safety_actions', 'safe_man_days'] },
  { title: 'Improvement & Appraisal (HR software)', keys: ['suggestions_received', 'suggestions_reviewed', 'suggestions_implemented', 'kaizens', 'kaizen_savings', 'poka_yokes', 'kpi_reviews_submitted', 'kpi_coverage_pct', 'avg_kpi_score'] },
  { title: 'Employee Relations & Cost (HR software)', keys: ['grievances', 'grievances_closed', 'disciplinary_cases', 'appreciation_letters', 'engagement_score', 'gross_salary_bill', 'personnel_expenses', 'pf_compliance', 'esi_compliance'] },
];

export default function HRSafety() {
  const ctx = useManualForm('hr_safety', DEFAULT);
  const canEdit = canEditSection(ctx.user, 'hr_safety', 'hr');
  const locked = ctx.currentCycle?.status === 'locked';
  const s = ctx.data.summary || DEFAULT.summary;
  const set = (k, v) => ctx.setData({ ...ctx.data, summary: { ...s, [k]: v } });
  const { hr, loading: hrLoading } = useHrMrm(ctx.currentCycle?.id);
  const F = hr?.fields || {};
  const fromHr = (formKey) => {
    const f = F[HR_KEY[formKey]];
    return f && f.state === 'loaded' && ((f.source || '').includes('hr_software') || f.source === 'hr_monthly' || f.source === 'none_recorded') ? f : null;
  };

  // Personnel % needs total sales, which lives in the Business Sales section.
  const [sales, setSales] = useState(0);
  useEffect(() => {
    if (!ctx.currentCycle) return;
    let active = true;
    api.get(`/entries/${ctx.currentCycle.id}/business_sales`)
      .then((r) => { if (active) setSales(Number(r.data?.data?.summary?.total_dispatched_value) || 0); })
      .catch(() => { if (active) setSales(0); });
    return () => { active = false; };
  }, [ctx.currentCycle?.id]);

  // Effective values: HR software first, then the manual entry (same priority the PPT uses).
  const eff = (formKey) => { const f = fromHr(formKey); return f ? f.value : s[formKey]; };
  const attritionVal = F.attrition_pct?.state === 'loaded' ? Number(F.attrition_pct.value)
    : (Number(eff('total_employees')) || 0) > 0 ? Number((((Number(eff('separations')) || 0) / Number(eff('total_employees'))) * 100).toFixed(1)) : null;
  const personnelVal = sales > 0 && eff('personnel_expenses') != null
    ? Number((((Number(eff('personnel_expenses')) || 0) / sales) * 100).toFixed(1)) : null;

  // Persist derived values so the saved entry (and the PPT) match what's shown.
  useEffect(() => {
    const next = { ...s };
    let changed = false;
    const a = attritionVal != null ? attritionVal : 0;
    const p = personnelVal != null ? personnelVal : 0;
    if ((Number(s.attrition_rate_pct) || 0) !== a) { next.attrition_rate_pct = a; changed = true; }
    if ((Number(s.personnel_expenses_pct_of_sales) || 0) !== p) { next.personnel_expenses_pct_of_sales = p; changed = true; }
    if (changed) ctx.setData({ ...ctx.data, summary: next });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attritionVal, personnelVal]);

  // A field the HR software supplies is shown read-only (no manual re-entry); others stay editable.
  const Field = ({ formKey, label, suffix }) => {
    const f = fromHr(formKey);
    if (f) return <ReadOnlyField label={label} value={f.display} hint={`Auto-fetched from HR software${f.source_field ? ` (${f.source_field.replace(/^units\[\d+\]\./, '')})` : ''}`} />;
    return <NumberField label={label} value={s[formKey]} onChange={(v) => set(formKey, v)} disabled={!canEdit || locked} suffix={suffix} />;
  };
  const Check = ({ formKey, label }) => {
    const f = fromHr(formKey);
    if (f) return <div className="text-sm flex items-center gap-2"><span className={`w-2 h-2 rounded-full ${f.value ? 'bg-[#22C55E]' : 'bg-[#EF4444]'}`} />{label}: <b>{f.display}</b> <span className="text-[10px] uppercase tracking-wider text-slate-400">HR software</span></div>;
    return <CheckboxField label={label} value={s[formKey]} onChange={(v) => set(formKey, v)} />;
  };

  const hrPresent = !!hr?.hr_present;
  const hrState = hr?.source_state?.state;

  return (
    <FormShell title="People, Safety & Culture" subtitle="Headcount, attendance, training, safety, suggestions — auto-fetched from the HR software; targets and anything HR does not record stay manual" status={ctx.status} save={ctx.save} submit={ctx.submit} saving={ctx.saving} submitting={ctx.submitting} canEdit={canEdit} locked={locked}>
      <div className={`rounded-2xl border p-4 flex items-start gap-3 ${hrPresent ? 'bg-[#F3EEFF] border-[#DDD6FE]' : hrState === 'unavailable' ? 'bg-[#FFF1F2] border-[#FECDD3]' : 'bg-[#FFF7E8] border-[#FDE68A]'}`} data-testid="hr-source-banner">
        <IconChip tone={hrPresent ? 'purple' : hrState === 'unavailable' ? 'red' : 'orange'} icon={Database} size={40} iconSize={18} />
        <div className="flex-1 min-w-0 text-sm">
          <div className="font-semibold text-[var(--aampl-text)] flex items-center gap-2 flex-wrap">
            {hrLoading ? 'Checking HR software data…' : hrPresent ? `HR software export loaded — ${hr.period} · unit ${hr.unit}` : hrState === 'unavailable' ? 'HR software upload failed for this period' : 'No HR software export for this unit/period'}
            {!hrLoading && <StatusBadge status={hrPresent ? 'uploaded' : hrState === 'unavailable' ? 'failed' : 'pending'} label={hrPresent ? `Source: ${hr.source_tag}` : hrState === 'unavailable' ? 'Data Unavailable' : 'Data Pending'} />}
          </div>
          <div className="text-xs text-slate-600 mt-0.5">{hr?.detail || 'Upload the HR app JSON backup on the Upload Center → HR card. Fields it carries are filled automatically and shown read-only here.'}</div>
          {hrPresent && <div className="text-[11px] text-slate-500 mt-1">{hr.counts?.hr_software} field(s) from HR software · {hr.counts?.hr_monthly || 0} from the HR monthly sheet · {hr.counts?.none_recorded || 0} safety counts at 0 (no incident) · {hr.counts?.manual} from this form · {hr.counts?.pending} pending</div>}
        </div>
        <a href="/uploads" className="outline-btn text-xs shrink-0"><RefreshCw size={13} /> Upload Center</a>
      </div>

      {hrPresent && (
        <Card title="HR Software — all fields fetched for this cycle">
          <div className="space-y-5">
            {GROUPS.map((g) => (
              <div key={g.title}>
                <div className="eyebrow mb-2">{g.title}</div>
                <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-2.5">
                  {g.keys.map((k) => {
                    const f = F[k] || {};
                    const pending = f.state !== 'loaded';
                    return (
                      <div key={k} className="rounded-xl border border-[var(--aampl-border)] bg-white/80 px-3 py-2.5 min-w-0" data-testid={`hr-field-${k}`}>
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 truncate" title={f.label}>{f.label || k}</div>
                        <div className={`font-bold tracking-tight mt-0.5 ${pending ? 'text-[13px] text-[#C2410C]' : 'text-lg text-[var(--aampl-text)]'}`}>{f.display || 'Data Pending'}</div>
                        <div className="text-[10px] text-slate-400 truncate">{sourceLabel(f.source)}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
            {Array.isArray(hr.not_recorded) && hr.not_recorded.length > 0 && (
              <div className="rounded-xl border border-[#FDE68A] bg-[#FFF7E8] p-3 text-xs text-[#9A3412]" data-testid="hr-not-recorded-page">
                <b>Not in the HR software:</b> {hr.not_recorded.map((c) => ({ attendance: 'attendance', trainings: 'training' }[c] || c)).join(', ')}. Fill the HR Monthly Sheet once a month (Upload Center → HR Monthly Sheet, template included) and these fill in automatically; safety counts with no incident show 0.
              </div>
            )}
            {Array.isArray(hr.category_wise) && hr.category_wise.length > 0 && (
              <div>
                <div className="eyebrow mb-2">Skill category (HR software)</div>
                <div className="flex flex-wrap gap-2">{hr.category_wise.map((d) => <span key={d.name} className="section-badge" style={{ background: '#ECFEFF', color: '#0E7490', borderColor: '#A5F3FC' }}>{d.name}: {d.count}</span>)}</div>
              </div>
            )}
            {Array.isArray(hr.department_wise) && hr.department_wise.length > 0 && (
              <div>
                <div className="eyebrow mb-2">Department-wise employees (HR software)</div>
                <div className="flex flex-wrap gap-2">{hr.department_wise.map((d) => <span key={d.name} className="section-badge" style={{ background: '#EEF3FF', color: '#3730A3', borderColor: '#C7D2FE' }}>{d.name}: {d.count}</span>)}</div>
              </div>
            )}
          </div>
        </Card>
      )}

      <Card title="Headcount">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field formKey="total_employees" label="Total Employees" />
          <Field formKey="new_joiners" label="New Joiners" />
          <Field formKey="separations" label="Separations" />
        </div>
      </Card>
      <Card title="Cost & Work Hours">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field formKey="personnel_expenses" label="Personnel Expenses (₹)" />
          <ReadOnlyField label="% of Sales (auto)" value={personnelVal != null ? `${personnelVal}%` : '—'} hint="Personnel Expenses ÷ Total Dispatched (Business Sales)" />
          <NumberField label="Target %" value={s.target_personnel_pct} onChange={(v) => set('target_personnel_pct', v)} disabled={!canEdit || locked} suffix="%" />
          <Field formKey="ot_hours" label="OT Hours" />
          <NumberField label="OT Target" value={s.ot_target} onChange={(v) => set('ot_target', v)} disabled={!canEdit || locked} />
        </div>
      </Card>
      <Card title="Attrition & Training">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <ReadOnlyField label="Attrition Rate % (auto)" value={attritionVal != null ? `${attritionVal}%` : 'Data Pending'} hint={F.attrition_pct?.state === 'loaded' ? `Source: ${sourceLabel(F.attrition_pct.source)}` : 'Separations ÷ Total Employees'} />
          <NumberField label="Attrition Target %" value={s.attrition_target_pct} onChange={(v) => set('attrition_target_pct', v)} disabled={!canEdit || locked} />
          <Field formKey="training_hours" label="Training Hours" />
          <NumberField label="Training Target" value={s.training_target_hours} onChange={(v) => set('training_target_hours', v)} disabled={!canEdit || locked} />
        </div>
      </Card>
      <Card title="Improvement">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Field formKey="suggestions_received" label="Suggestions Received" />
          <Field formKey="suggestions_implemented" label="Implemented" />
          <Field formKey="kaizens" label="Kaizens" />
          <Field formKey="poka_yokes" label="Poka-Yokes" />
        </div>
      </Card>
      <Card title="Safety">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field formKey="accidents_lti" label="LTI Accidents" />
          <Field formKey="accidents_non_lti" label="Non-LTI Accidents" />
          <Field formKey="near_misses" label="Near Misses" />
        </div>
      </Card>
      <Card title="Statutory Compliance">
        <div className="flex gap-6 flex-wrap">
          <Check formKey="pf_compliance" label="PF Compliance" />
          <Check formKey="esi_compliance" label="ESI Compliance" />
        </div>
        <div className="mt-3"><TextAreaField label="Notes" value={s.notes} onChange={(v) => set('notes', v)} disabled={!canEdit || locked} /></div>
      </Card>
    </FormShell>
  );
}
